// @soft-depth
import React, { useState } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { AlertTriangle, Handshake, Lightbulb, Pencil } from 'lucide-react';
import DISCDialogComponent from '../DISCDialog/DISCDialogComponent/DISCDialogComponent';
import { ADVISOR_DISC_TYPES, getDiscChemistry, useAdvisorDisc } from './discChemistry';
import type { AdvisorDiscType, DiscChemistryMode } from './discChemistry';
import { discLabel, discTag } from '../../../constants/disc';
import { IdeaCard } from './IdeaKit';
import LegacyThemeScope from '../../../design-system/LegacyThemeScope';

/**
 * 我的溝通風格 — the adviser sets their own DISC letter and reads the blind
 * spot and strategy for meeting this person's letter. The letter is set through
 * the app's DISC dialog rather than four bare letter buttons: its 外向/內向 ×
 * 理性/感性 toggles let an adviser who doesn't know DISC still land on one.
 *
 * Shared by 招募 IDEA's 溝通方式 page (`mode="recruit"`, the other person is a
 * candidate) and Smart IDEA's 溝通方式 tab (`mode="business"`, a client). Only
 * the advice and the words for "the other person" change with the mode, never
 * the colours (blue in both, as before the redesign); the adviser's letter is shared (see discChemistry.ts).
 */

type IntlLike = { formatMessage: (descriptor: { id: string }, values?: Record<string, unknown>) => string };

// zh-Hant-HK fallbacks, used when a message id is missing (e.g. in tests).
const MODE_FALLBACKS: Record<DiscChemistryMode, { hint: string; needDisc: string; defaultName: string }> = {
  recruit: {
    hint: '點擊上方按鈕設定你的風格，解鎖針對此候選人的 16 種跨性格破局與盲點預警攻略。',
    needDisc: '候選人尚未設定 DISC 性格，在「候選人概覽」的角色卡設定後即可解鎖專屬破局對策。',
    defaultName: '候選人'
  },
  business: {
    hint: '點擊上方按鈕設定你的風格，查看與此客戶溝通時的盲點提示與應對策略。',
    needDisc: '客戶尚未設定 DISC 性格，在「客戶概覽」的角色卡設定後即可查看專屬溝通策略。',
    defaultName: '客戶'
  }
};

export type DiscChemistryCardProps = {
  mode: DiscChemistryMode;
  /** The candidate's or client's name, for the pairing pill. */
  name: string;
  /** Their DISC letter on file; anything else reads as "not set". */
  disc: string | null | undefined;
  intl: IntlLike;
};

const isAdvisorDisc = (value?: string | null): value is AdvisorDiscType =>
  ADVISOR_DISC_TYPES.includes(value as AdvisorDiscType);

const DiscChemistryCard = (props: DiscChemistryCardProps) => {
  const { mode, name, disc, intl } = props;
  const [advisorDisc, setAdvisorDisc] = useAdvisorDisc();
  const [pickerOpen, setPickerOpen] = useState(false);
  const locale = (intl as IntlLike & { locale?: string }).locale || 'zh-Hant-HK';

  const chemistry = getDiscChemistry(advisorDisc, disc, locale, mode);
  const otherUpper = disc?.toUpperCase();
  const otherTag = discTag(otherUpper, locale);
  const advisorTag = discTag(advisorDisc, locale);
  const fallbacks = MODE_FALLBACKS[mode];

  const t = (id: string, fallback?: string, values?: Record<string, unknown>): string => {
    try {
      const msg = intl.formatMessage({ id }, values);
      return msg && msg !== id ? msg : fallback || id;
    } catch (_) {
      return fallback || id;
    }
  };
  const displayName = name || t(`disc-chemistry-default-name-${mode}`, fallbacks.defaultName);

  // 盲點 / 破局策略 — a warning and its answer, side by side on a wide screen.
  const advicePanel = (
    testId: string,
    tone: 'warning' | 'success',
    icon: React.ReactNode,
    title: string,
    body: string
  ) => (
    <Paper
      elevation={0}
      data-testid={testId}
      sx={{
        p: 1.5,
        borderRadius: 1.25,
        border: '1px solid',
        borderColor: `${tone}.border`,
        bgcolor: `${tone}.soft`,
        color: `${tone}.dark`
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75, mb: 0.75 }}>
        {icon}
        <Typography variant="subtitle2" component="span" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </Stack>
      <Typography variant="body2">{body}</Typography>
    </Paper>
  );

  return (
    // The same section card as the rest of 溝通方式 in both IDEAs. IdeaCard
    // mounts the Soft Depth scope its head (this action chip) and body render in.
    <IdeaCard
      id="chemistry"
      testId="disc-chemistry"
      data-mode={mode}
      icon={<Handshake size={18} aria-hidden="true" />}
      title={t('disc-chemistry-advisor-label', '我的溝通風格')}
      action={
        <Chip
          component="button"
          type="button"
          clickable
          color="primary"
          variant={advisorTag ? 'filled' : 'outlined'}
          onClick={() => setPickerOpen(true)}
          data-testid="advisor-disc-button"
          data-disc={advisorDisc || ''}
          label={
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
              {discLabel(advisorDisc, locale) || t('disc-chemistry-advisor-set', '設定我的風格')}
              <Pencil size={12} aria-hidden="true" />
            </Box>
          }
          // Set: the pale pill with a full accent outline, as before the redesign.
          sx={{
            ml: 'auto',
            flexShrink: 0,
            ...(advisorTag && { '&, &:hover, &.Mui-focusVisible': { borderColor: 'primary.main' } })
          }}
        />
      }
    >
      <LegacyThemeScope>
        <DISCDialogComponent
          open={pickerOpen}
          contentTextType="adviser"
          personality={advisorDisc || undefined}
          onClose={() => setPickerOpen(false)}
          onSubmit={(letter: string | undefined) => setAdvisorDisc(isAdvisorDisc(letter) ? letter : null)}
          // Sits above the 招募 / Smart IDEA bottom sheet on mobile.
          style={{ zIndex: 2000 }}
        />
      </LegacyThemeScope>

      {/* 跨性格對配與盲點卡片內容 */}
      {advisorTag && otherTag && chemistry ? (
        <Box data-testid="disc-chemistry-paired">
          {/* 配對徽章列 */}
          <Stack
            direction="row"
            sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.25, mb: 1.5 }}
          >
            <Chip
              label={
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  <span>{t('disc-chemistry-advisor-pill', `你：${advisorTag}`, { type: advisorTag })}</span>
                  <Box component="span" aria-hidden="true" sx={{ color: 'text.disabled' }}>
                    ✕
                  </Box>
                  <span>
                    {t('disc-chemistry-other-pill', `${displayName}：${otherTag}`, {
                      name: displayName,
                      type: otherTag
                    })}
                  </span>
                </Box>
              }
              sx={{ borderRadius: 1, color: 'text.primary', fontWeight: 500 }}
            />
            <Chip variant="outlined" label={chemistry.dynamic} sx={{ borderRadius: 1, color: 'text.primary' }} />
          </Stack>

          {/* 盲點 (Warning) 與 破局策略 (Strategy) 雙欄卡片 */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            {advicePanel(
              'disc-chemistry-blindspot',
              'warning',
              <AlertTriangle size={15} aria-hidden="true" />,
              t('disc-chemistry-blindspot-title', '你的潛在溝通盲點'),
              chemistry.blindSpot
            )}
            {advicePanel(
              'disc-chemistry-strategy',
              'success',
              <Lightbulb size={15} aria-hidden="true" />,
              t('disc-chemistry-strategy-title', '破局策略與心法'),
              chemistry.strategy
            )}
          </Box>
        </Box>
      ) : (
        <Typography
          variant="body2"
          component="p"
          data-testid={!advisorDisc ? 'disc-chemistry-empty-advisor' : 'disc-chemistry-empty-other'}
          sx={{ m: 0, py: 1, textAlign: 'center', color: 'text.secondary' }}
        >
          {!advisorDisc
            ? t(`disc-chemistry-hint-${mode}`, fallbacks.hint)
            : t(`disc-chemistry-need-disc-${mode}`, fallbacks.needDisc)}
        </Typography>
      )}
    </IdeaCard>
  );
};

export default DiscChemistryCard;
